/**
 * EcommerceCommissionControl — iter-299 (Feb 2026)
 * =================================================
 *
 * Admin control for the markup applied to every merchant-platform
 * product price before customers see it.
 *
 * Two independent switches:
 *  1. Commission (%)   — e.g., 5% of the product price
 *  2. Admin fee (AED)  — flat amount added once per listing
 *
 * Merchants type the raw product price (e.g., 1,000 AED). On the
 * customer side, both markups are added automatically:
 *   customer = raw * (1 + commission/100) + admin_fee
 *
 * Nothing of CloudGM's cut is exposed to the customer; the final
 * price simply looks higher by the admin-configured amounts.
 *
 * Route: /control/cloudgm-business/ecommerce-commission
 * Admin API:  GET / PUT  /api/admin/ecommerce-commission
 * Public API: GET        /api/public/ecommerce-commission
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Percent, Coins, Save, Loader2, ArrowLeft, ShieldCheck, ArrowRight,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  commission_enabled: false,
  commission_percent: 0,
  admin_fee_enabled:  false,
  admin_fee_amount:   0,
};

export default function EcommerceCommissionControl() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [form, setForm] = useState(DEFAULTS);
  const [initial, setInitial] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const auth = () => ({ headers: getAuthHeaders() });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/ecommerce-commission`, auth());
        const d = r.data?.data || {};
        const next = {
          commission_enabled: !!d.commission_enabled,
          commission_percent: Number(d.commission_percent || 0),
          admin_fee_enabled:  !!d.admin_fee_enabled,
          admin_fee_amount:   Number(d.admin_fee_amount || 0),
        };
        setForm(next);
        setInitial(next);
        setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
      } catch (e) {
        toast.error(t('تعذّر تحميل الإعدادات', 'Failed to load settings'));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (patch) => setForm((p) => ({ ...p, ...patch }));

  const isDirty = useMemo(() => (
    form.commission_enabled !== initial.commission_enabled
    || Number(form.commission_percent) !== Number(initial.commission_percent)
    || form.admin_fee_enabled !== initial.admin_fee_enabled
    || Number(form.admin_fee_amount) !== Number(initial.admin_fee_amount)
  ), [form, initial]);

  // Live preview: AED 1,000 product → shown customer price with current settings.
  const sampleRaw = 1000;
  const preview = useMemo(() => {
    let p = sampleRaw;
    if (form.commission_enabled) p = p * (1 + (Number(form.commission_percent) || 0) / 100);
    if (form.admin_fee_enabled)  p = p + (Number(form.admin_fee_amount) || 0);
    return Math.round(p * 100) / 100;
  }, [form]);

  const save = async () => {
    // Light validation before hitting the API — Pydantic covers the rest.
    if (form.commission_enabled && (Number(form.commission_percent) <= 0 || Number(form.commission_percent) > 100)) {
      toast.error(t('نسبة العمولة يجب أن تكون بين 0.01 و 100', 'Commission must be between 0.01 and 100'));
      return;
    }
    if (form.admin_fee_enabled && Number(form.admin_fee_amount) < 0) {
      toast.error(t('الرسوم الإدارية لا يمكن أن تكون سالبة', 'Admin fee cannot be negative'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        commission_enabled: !!form.commission_enabled,
        commission_percent: Number(form.commission_percent) || 0,
        admin_fee_enabled:  !!form.admin_fee_enabled,
        admin_fee_amount:   Number(form.admin_fee_amount) || 0,
      };
      const r = await axios.put(`${API}/api/admin/ecommerce-commission`, payload, auth());
      const d = r.data?.data || {};
      setInitial({
        commission_enabled: !!d.commission_enabled,
        commission_percent: Number(d.commission_percent || 0),
        admin_fee_enabled:  !!d.admin_fee_enabled,
        admin_fee_amount:   Number(d.admin_fee_amount || 0),
      });
      setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
      toast.success(t('تم حفظ الإعدادات', 'Settings saved'));
    } catch (e) {
      toast.error(t('فشل الحفظ', 'Save failed'));
    } finally { setSaving(false); }
  };

  const fmtAed = (n) => Number(n || 0).toLocaleString(isAr ? 'ar-AE' : 'en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  return (
    <DashboardLayout
      title="التحكم بعمولة المتاجر الإلكترونية"
      titleEn="E-commerce Store Commission"
      subtitle="تفعيل نسبة العمولة والرسوم الإدارية التي تُضاف تلقائياً إلى أسعار منتجات منصة التجار"
      subtitleEn="Enable commission % and admin fee that are added automatically to merchant platform product prices"
      screenCode="BIZ-C06"
      backPath="/dashboard/finance/settings"
      onBack={() => navigate('/dashboard/finance/settings')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'إعدادات القسم المالي', labelEn: 'Finance Settings', path: '/dashboard/finance/settings' },
        { labelAr: 'عمولة المتاجر الإلكترونية', labelEn: 'E-commerce Commission' },
      ]}
    >
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="ecommerce-commission-root">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 text-white/40 animate-spin" />
          </div>
        ) : (
          <>
            {/* How it works banner */}
            <div className="rounded-xl border border-sky-500/20 bg-sky-500/5 p-4 mb-6 flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
              <div className="text-xs text-white/70 leading-relaxed" data-testid="ecc-explainer">
                {isAr ? (
                  <>
                    التاجر يُدخل السعر الأساسي (مثال 1,000 درهم). منصة التجار تقرأ هذه الإعدادات
                    وتضيف تلقائياً النسبة ثم الرسوم الإدارية — ثم يُعرض السعر النهائي للعميل.
                    نسبة الشركة لا تظهر للعميل أبداً. يمكن تفعيل أو إيقاف كل حقل باستقلال.
                    <br />
                    <code className="text-sky-300 text-[10px]" dir="ltr">
                      customer = price × (1 + commission%/100) + admin_fee
                    </code>
                  </>
                ) : (
                  <>
                    Merchants type the raw price (e.g., AED 1,000). The merchant platform
                    reads these settings and auto-adds the commission + flat admin fee
                    before showing the price to the customer. CloudGM&apos;s cut is never
                    shown. Each switch is independent.
                    <br />
                    <code className="text-sky-300 text-[10px]" dir="ltr">
                      customer = price × (1 + commission%/100) + admin_fee
                    </code>
                  </>
                )}
              </div>
            </div>

            {/* ──────────── Commission block ──────────── */}
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-5"
              data-testid="ecc-commission-block"
            >
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <Percent className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{t('نسبة عمولة الشركة', 'Company Commission %')}</h3>
                    <p className="text-xs text-white/50">
                      {t('تُضاف كنسبة مئوية من السعر الأساسي للمنتج',
                         'Added as a percentage of the raw product price')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={form.commission_enabled}
                  onCheckedChange={(v) => upd({ commission_enabled: v })}
                  data-testid="ecc-commission-toggle"
                />
              </div>
              <div className={`${form.commission_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'} transition-opacity`}>
                <Label className="text-[11px] text-white/60 mb-2 block">
                  {t('النسبة المئوية (%)', 'Percent (%)')}
                </Label>
                <div className="flex items-center gap-2 max-w-xs">
                  <Input
                    type="number"
                    min="0" max="100" step="0.01"
                    value={form.commission_percent}
                    onChange={(e) => upd({ commission_percent: e.target.value })}
                    disabled={!form.commission_enabled}
                    className="bg-black/40 border-white/10 text-white"
                    dir="ltr"
                    data-testid="ecc-commission-percent"
                  />
                  <span className="text-white/50 text-sm">%</span>
                </div>
                <p className="text-[11px] text-white/40 mt-2">
                  {t('مثال: 5% تعني أن 1,000 درهم تصبح 1,050 درهم للعميل',
                     'E.g., 5% means AED 1,000 becomes AED 1,050 for the customer')}
                </p>
              </div>
            </motion.div>

            {/* ──────────── Admin fee block ──────────── */}
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-5"
              data-testid="ecc-adminfee-block"
            >
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                    <Coins className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{t('الرسوم الإدارية (مبلغ ثابت)', 'Admin Fee (Flat AED)')}</h3>
                    <p className="text-xs text-white/50">
                      {t('مبلغ ثابت يُضاف لكل منتج بعد العمولة',
                         'A flat amount added to each product after commission')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={form.admin_fee_enabled}
                  onCheckedChange={(v) => upd({ admin_fee_enabled: v })}
                  data-testid="ecc-adminfee-toggle"
                />
              </div>
              <div className={`${form.admin_fee_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'} transition-opacity`}>
                <Label className="text-[11px] text-white/60 mb-2 block">
                  {t('المبلغ (AED)', 'Amount (AED)')}
                </Label>
                <div className="flex items-center gap-2 max-w-xs">
                  <Input
                    type="number"
                    min="0" step="0.01"
                    value={form.admin_fee_amount}
                    onChange={(e) => upd({ admin_fee_amount: e.target.value })}
                    disabled={!form.admin_fee_enabled}
                    className="bg-black/40 border-white/10 text-white"
                    dir="ltr"
                    data-testid="ecc-adminfee-amount"
                  />
                  <span className="text-white/50 text-sm">AED</span>
                </div>
                <p className="text-[11px] text-white/40 mt-2">
                  {t('مثال: 5 درهم تُضاف لكل منتج بعد تطبيق النسبة',
                     'E.g., AED 5 is added to each product after commission is applied')}
                </p>
              </div>
            </motion.div>

            {/* ──────────── Live preview ──────────── */}
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 mb-5" data-testid="ecc-preview">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                {t('معاينة فورية', 'Live Preview')}
              </h3>
              <div className="flex items-center justify-between gap-4 flex-wrap text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-white/50">{t('سعر المنتج الأساسي:', 'Raw product price:')}</span>
                  <span className="text-white font-mono" dir="ltr">AED {fmtAed(sampleRaw)}</span>
                </div>
                <ArrowRight className={`h-4 w-4 text-white/30 ${isAr ? 'rotate-180' : ''}`} />
                <div className="flex items-center gap-2">
                  <span className="text-white/50">{t('السعر النهائي للعميل:', 'Final customer price:')}</span>
                  <span className="text-emerald-300 font-mono text-lg font-bold" dir="ltr"
                        data-testid="ecc-preview-final">
                    AED {fmtAed(preview)}
                  </span>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/40 flex flex-wrap gap-x-4 gap-y-1">
                {form.commission_enabled && (
                  <span data-testid="ecc-preview-commline">
                    + {t('عمولة', 'commission')} {Number(form.commission_percent) || 0}%
                    &nbsp;= AED {fmtAed(sampleRaw * (Number(form.commission_percent) || 0) / 100)}
                  </span>
                )}
                {form.admin_fee_enabled && (
                  <span data-testid="ecc-preview-feeline">
                    + {t('رسوم إدارية', 'admin fee')} = AED {fmtAed(Number(form.admin_fee_amount) || 0)}
                  </span>
                )}
                {!form.commission_enabled && !form.admin_fee_enabled && (
                  <span className="text-amber-300/80" data-testid="ecc-preview-none">
                    {t('كلا الحقلين متوقّف — السعر يظهر للعميل كما هو من التاجر.',
                       'Both switches are OFF — customer sees exactly what the merchant typed.')}
                  </span>
                )}
              </div>
            </div>

            {/* Meta */}
            {meta.updated_at && (
              <div className="text-[11px] text-white/40 mb-5" dir="ltr">
                {t('آخر تحديث:', 'Last updated:')} {new Date(meta.updated_at).toLocaleString()} · {meta.updated_by}
              </div>
            )}

            {/* Save bar */}
            <div className="rounded-2xl border border-white/10 bg-black/50 p-3 flex items-center justify-between gap-3">
              <button onClick={() => navigate('/dashboard/finance/settings')}
                      className="text-xs text-white/50 hover:text-white/80 flex items-center gap-1 transition-colors"
                      data-testid="ecc-back">
                <ArrowLeft className={`h-3.5 w-3.5 ${isAr ? 'rotate-180' : ''}`} />
                {t('رجوع', 'Back')}
              </button>
              <Button
                onClick={save}
                disabled={!isDirty || saving}
                className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold disabled:opacity-50"
                data-testid="ecc-save-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" />
                        : <Save className="h-3.5 w-3.5 me-1.5" />}
                {t('حفظ الإعدادات', 'Save Settings')}
              </Button>
            </div>
          </>
        )}
      </section>
    </DashboardLayout>
  );
}
